import { activeRegionAtom } from "@/atoms/activeRegion";
import { homeSpotAtom } from "@/atoms/homeRegion";
import { multiCamAtom } from "@/atoms/multiCam";
import { usePageRedirect } from "@/hooks/usePageRedirect";
import { usePageView } from "@/hooks/usePageView";
import AuthGate from "@/components/Auth/AuthGate";
import Layout from "@/components/Layout";
import Player from "@/components/Player/Player";
import { Camera } from "@/lib/regions";
import {
  trackHomeSpotClear,
  trackHomeSpotSet,
  trackMultiCamAdd,
  trackMultiCamRemove,
} from "@/lib/analytics";
import {
  useRegion,
  useZones,
  getCamerasFromRegion,
  findRegionInZones,
} from "@/hooks/useRegions";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

export default function MultiCamPage() {
  const { regionId: urlRegionId } = useParams();
  const fallbackRegion = useRecoilValue(activeRegionAtom);
  const [savedConfig, setSavedConfig] = useRecoilState(multiCamAtom);
  const [homeSpot, setHomeSpot] = useRecoilState(homeSpotAtom);
  const navigate = useNavigate();

  // Resolve region: URL param > saved config > active region
  const resolvedRegion = urlRegionId
    ? decodeURIComponent(urlRegionId)
    : (savedConfig?.regionId ?? fallbackRegion);

  const { data: regionData } = useRegion(resolvedRegion);
  const { data: zones } = useZones();
  const found = zones ? findRegionInZones(zones, resolvedRegion) : null;

  const availableCameras = useMemo(
    () => (regionData ? getCamerasFromRegion(regionData) : []),
    [regionData],
  );

  // Hydrate camera grid from saved config or default to first 2
  const initializedRef = useRef(false);

  // Derive the displayed cameras from the saved config
  const displayCameras = useMemo(() => {
    if (availableCameras.length === 0) return [];

    // If saved config matches this region, restore those cameras
    if (savedConfig?.regionId === resolvedRegion && savedConfig.cameraIds.length > 0) {
      const restored = savedConfig.cameraIds
        .map((id) => availableCameras.find((c) => c.id === id))
        .filter((c): c is Camera => c != null);
      if (restored.length > 0) return restored;
    }

    // Default: first 2 cameras (or all if only 1)
    return availableCameras.slice(0, Math.min(2, availableCameras.length));
  }, [availableCameras, savedConfig, resolvedRegion]);

  // Resolve automatic URL normalization before counting this page.
  const readyForPageView = usePageRedirect(
    !urlRegionId && resolvedRegion
      ? `/multicam/${encodeURIComponent(resolvedRegion)}`
      : null,
  );
  usePageView(readyForPageView);

  // Persist helper
  const persist = useCallback(
    (cameras: Camera[]) => {
      setSavedConfig({
        regionId: resolvedRegion,
        cameraIds: cameras.map((c) => c.id),
      });
    },
    [resolvedRegion, setSavedConfig],
  );

  // Persist on first render with actual cameras (handles default case)
  useEffect(() => {
    if (initializedRef.current) return;
    if (displayCameras.length === 0) return;
    initializedRef.current = true;

    // Only persist if no saved config or region changed
    if (savedConfig?.regionId !== resolvedRegion || savedConfig.cameraIds.length === 0) {
      persist(displayCameras);
    }
  }, [displayCameras, savedConfig, resolvedRegion, persist]);

  const addCamera = useCallback(() => {
    if (availableCameras.length === 0) return;
    const next =
      availableCameras.find((c) => !displayCameras.some((ac) => ac.url === c.url)) ??
      availableCameras[0];
    const updated = [...displayCameras, next];
    persist(updated);
    trackMultiCamAdd();
  }, [availableCameras, displayCameras, persist]);

  const updateCamera = useCallback(
    (index: number, camera: Camera) => {
      const updated = [...displayCameras];
      updated[index] = camera;
      persist(updated);
    },
    [displayCameras, persist],
  );

  const deleteCamera = useCallback(
    (index: number) => {
      const updated = displayCameras.filter((_, i) => i !== index);
      persist(updated);
      trackMultiCamRemove();
    },
    [displayCameras, persist],
  );

  const regionLabel = found?.region.label || resolvedRegion;
  const isSingleCamera = displayCameras.length === 1;

  const isHomeMultiCam =
    homeSpot?.preferMultiCam === true && homeSpot?.regionId === resolvedRegion;

  const toggleHomeSpot = useCallback(() => {
    if (isHomeMultiCam) {
      setHomeSpot(null);
      trackHomeSpotClear();
    } else {
      setHomeSpot({ regionId: resolvedRegion, cameraId: "", preferMultiCam: true });
      // `camera_id` is null for multi-cam home spots; the multi-cam
      // origin is implicit because this is the only call site that
      // omits a camera_id.
      trackHomeSpotSet(resolvedRegion, null);
    }
  }, [isHomeMultiCam, resolvedRegion, setHomeSpot]);

  return (
    <Layout>
      <AuthGate
        source="multicam"
        title="Watch every cam at once."
        reason="Sign in to open the multi-cam grid. It streams every public camera in a region side-by-side."
      >
        <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 2 } }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => navigate("/")}
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                sx={{
                  color: "text.secondary",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Back
              </Button>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                Multi-Cam — {regionLabel}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                onClick={toggleHomeSpot}
                startIcon={
                  isHomeMultiCam ? (
                    <StarIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <StarBorderIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  color: isHomeMultiCam ? "#ffc107" : "text.secondary",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  minWidth: "auto",
                  "&:hover": {
                    color: isHomeMultiCam ? "#ffca28" : "primary.main",
                  },
                }}
              >
                {isHomeMultiCam ? "Home View" : "Set Home"}
              </Button>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              >
                {displayCameras.length} of {availableCameras.length} cams
              </Typography>
            </Box>
          </Box>

          {/* Camera grid */}
          {displayCameras.length > 0 && (
            <Grid container spacing={1.5}>
              {displayCameras.map((camera, index) => (
                <Grid
                  item
                  key={`${camera.url}-${index}`}
                  xs={12}
                  md={isSingleCamera ? 12 : 6}
                >
                  <Player
                    index={index}
                    name={camera.name}
                    url={camera.url}
                    camera={camera}
                    availableCameras={availableCameras}
                    canDelete={displayCameras.length > 1}
                    onCameraChange={(cam) => updateCamera(index, cam)}
                    onDelete={() => deleteCamera(index)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Add camera */}
          {displayCameras.length < availableCameras.length && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
                mb: 1,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCamera}
                sx={{
                  color: "text.secondary",
                  borderColor: "rgba(255,255,255,0.1)",
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    borderColor: "rgba(79,195,247,0.3)",
                    color: "primary.main",
                  },
                }}
              >
                Add Camera
              </Button>
            </Box>
          )}
        </Container>
      </AuthGate>
    </Layout>
  );
}
